import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SalviaLayout from "@/components/SalviaLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  countAccessibleModules,
  getModuleAccessByKey,
  isNr1HiddenForRole,
  resolveNr1OverviewRoute,
  resolvePortalServiceRoute,
  type PortalShellModuleAccess,
  type PortalShellService,
} from "@/lib/portalShell";
import { trpc } from "@/lib/trpc";
import {
  ArrowRight,
  BellRing,
  Brain,
  Building2,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  UserCircle2,
  Users,
} from "lucide-react";
import { useMemo } from "react";
import { useLocation } from "wouter";

const ORDERED_PORTAL_MODULES = ["nr1", "telemedicina", "aps_fisica", "aps_digital", "insights_ia"] as const;

type PortalCatalogItem = {
  key: string;
  name: string;
  description?: string;
  status: "active" | "planned";
  serviceName?: string;
};

type HomeServiceCard = {
  key: string;
  title: string;
  description: string;
  href?: string;
  isReady: boolean;
  isVisible: boolean;
  ctaLabel: string;
};

function getServiceRoute(moduleKey: string, nr1OverviewRoute: string) {
  if (moduleKey === "nr1") return nr1OverviewRoute;
  return resolvePortalServiceRoute(moduleKey);
}

function getServiceIcon(moduleKey: string) {
  if (moduleKey === "nr1") return <ShieldCheck className="h-5 w-5" />;
  if (moduleKey === "telemedicina") return <Stethoscope className="h-5 w-5" />;
  if (moduleKey === "aps_fisica") return <Users className="h-5 w-5" />;
  if (moduleKey === "aps_digital") return <Sparkles className="h-5 w-5" />;
  return <Brain className="h-5 w-5" />;
}

function getFriendlyProfileLabel(rawLabel?: string | null) {
  const value = (rawLabel || "").trim();
  const normalized = value.toLowerCase();

  if (!value) return "Seu acesso principal";
  if (normalized.includes("rh") && normalized.includes("cliente")) return "Responsável da empresa";
  if (normalized.includes("client") || normalized.includes("cliente")) return "Acesso da empresa";
  if (normalized.includes("channel") || normalized.includes("canal")) return "Acesso do canal";
  if (normalized.includes("admin")) return "Acesso de gestão";
  if (normalized.includes("consult")) return "Acesso de consultoria";

  return value;
}

export default function PortalHome() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const platformNucleus = (user as any)?.platformNucleus;
  const currentProfile = platformNucleus?.currentProfile;
  const currentScope = platformNucleus?.currentScope;
  const aiEntitlements = platformNucleus?.aiEntitlements ?? { hasAIInsights: false };
  const companyServices = (platformNucleus?.services || []) as PortalShellService[];
  const moduleAccess = (platformNucleus?.modules || []) as PortalShellModuleAccess[];
  const catalog = (platformNucleus?.catalog || []) as PortalCatalogItem[];

  const workspaceQuery = trpc.portalAccount.getWorkspace.useQuery(undefined, {
    enabled: !!user,
    staleTime: 30_000,
  });

  const userRole = typeof user?.role === "string" ? user.role : "";
  const nr1OverviewRoute = resolveNr1OverviewRoute(userRole);
  const accessibleModulesCount = countAccessibleModules(moduleAccess);

  const rawProfileLabel = currentProfile?.label || workspaceQuery.data?.identity.roleLabel || "";
  const profileLabel = getFriendlyProfileLabel(rawProfileLabel);
  const companyLabel = currentScope?.company?.displayName || currentScope?.channel?.displayName || "Sua conta principal";
  const userName = String(workspaceQuery.data?.identity.name || (user as any)?.name || "").trim();
  const firstName = userName.split(" ")[0] || "";
  const communicationItem = workspaceQuery.data?.visibleItems?.[0] ?? null;

  const catalogByKey = useMemo(() => new Map(catalog.map((item) => [item.key, item])), [catalog]);
  const serviceByKey = useMemo(() => new Map(companyServices.map((item) => [item.moduleKey, item])), [companyServices]);

  const serviceCards = useMemo<HomeServiceCard[]>(() => {
    return ORDERED_PORTAL_MODULES.map((moduleKey) => {
      const access = getModuleAccessByKey(moduleAccess, moduleKey);
      const service = serviceByKey.get(moduleKey);
      const catalogItem = catalogByKey.get(moduleKey);

      const title = catalogItem?.serviceName || catalogItem?.name || service?.serviceName || moduleKey.toUpperCase();
      const description =
        moduleKey === "nr1"
          ? "Importação, pessoas, documentos e rotinas do NR1."
          : moduleKey === "telemedicina"
            ? "Atendimento e acompanhamento de saúde quando o serviço estiver liberado."
            : moduleKey === "aps_fisica"
              ? "Acompanhamento físico e informações relacionadas ao cuidado presencial."
              : moduleKey === "aps_digital"
                ? "Jornada digital de cuidado quando este serviço estiver disponível."
                : "Perguntas rápidas para ajudar você a entender melhor o que já aparece no portal.";

      const hasOperationalAccess = !!access?.userHasOperationalAccess;
      const hasVisibleAccess = !!access?.userHasVisibleAccess;
      const hasVisiblePortalAccess = moduleKey === "insights_ia"
        ? !!aiEntitlements.hasAIInsights && hasVisibleAccess
        : hasVisibleAccess;

      return {
        key: moduleKey,
        title,
        description,
        href: getServiceRoute(moduleKey, nr1OverviewRoute),
        isReady: hasOperationalAccess,
        isVisible: hasVisiblePortalAccess,
        ctaLabel: moduleKey === "nr1" ? "Entrar no NR1" : "Abrir serviço",
      };
    }).filter((item) => !(item.key === "nr1" && isNr1HiddenForRole(userRole)) && (item.isVisible || item.isReady));
  }, [aiEntitlements.hasAIInsights, catalogByKey, moduleAccess, nr1OverviewRoute, serviceByKey, userRole]);

  const highlightedServices = serviceCards.slice(0, 3);

  return (
    <SalviaLayout title="Portal da Jornada do Cliente">
      <div className="space-y-6">
        <section className="rounded-[2rem] bg-slate-950 p-6 text-white shadow-sm lg:p-8">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-200">
              <Building2 className="h-3.5 w-3.5 text-[#C0FF00]" />
              Portal da Jornada do Cliente
            </div>

            <div className="space-y-3">
              <h1 className="max-w-4xl text-3xl font-semibold tracking-tight text-white lg:text-4xl">
                {firstName ? `Olá, ${firstName}. Que bom ter você por aqui.` : "Que bom ter você por aqui."}
              </h1>
              <p className="max-w-3xl text-sm leading-7 text-slate-300 lg:text-base">
                Esta é a sua entrada principal no portal. Aqui você encontra seus dados essenciais e segue com tranquilidade para o serviço certo.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-3xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Seu acesso</p>
                <p className="mt-2 text-sm font-semibold text-white">{profileLabel}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Empresa atual</p>
                <p className="mt-2 text-sm font-semibold text-white">{companyLabel}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Serviços disponíveis</p>
                <p className="mt-2 text-sm font-semibold text-white">{accessibleModulesCount} disponível(is) agora</p>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-lime-100 text-lime-700">
              <BellRing className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Mensagem de entrada</p>
                  <h2 className="mt-1 text-2xl font-semibold text-slate-950">Mensagem de boas-vindas</h2>
                </div>
                {communicationItem?.eyebrow ? (
                  <Badge variant="secondary" className="rounded-full">{communicationItem.eyebrow}</Badge>
                ) : null}
              </div>

              {workspaceQuery.isLoading ? (
                <div className="mt-6 space-y-3">
                  <Skeleton className="h-8 w-56 rounded-xl" />
                  <Skeleton className="h-5 w-full rounded-xl" />
                  <Skeleton className="h-20 w-full rounded-2xl" />
                  <Skeleton className="h-10 w-40 rounded-xl" />
                </div>
              ) : communicationItem ? (
                <div className="mt-6 rounded-[1.75rem] border border-slate-200 bg-slate-50 p-5">
                  <h3 className="text-xl font-semibold text-slate-950">{communicationItem.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{communicationItem.summary}</p>
                  <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm leading-7 text-slate-600">
                    {communicationItem.body}
                  </div>
                  {communicationItem.ctaLabel && communicationItem.ctaHref ? (
                    <Button className="mt-5" onClick={() => setLocation(communicationItem.ctaHref!)}>
                      {communicationItem.ctaLabel}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className="mt-6 rounded-[1.75rem] border border-dashed border-slate-300 bg-slate-50 p-5 text-sm leading-7 text-slate-600">
                  Nenhum aviso foi publicado para este perfil até o momento.
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Escolha por onde continuar</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                Os serviços mais importantes para a sua rotina aparecem primeiro. O restante pode entrar depois, quando fizer sentido para você.
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 xl:grid-cols-3">
            {highlightedServices.map((service) => (
              <article key={service.key} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-white p-3 text-slate-700 shadow-sm">{getServiceIcon(service.key)}</div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900">{service.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{service.description}</p>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-between gap-3">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      service.isReady ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {service.isReady ? "Pronto para uso" : "Disponível para consulta"}
                  </span>
                  <Button onClick={() => service.href && setLocation(service.href)} disabled={!service.href} className="min-w-36">
                    {service.ctaLabel}
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </section>

        {serviceCards.length > highlightedServices.length ? (
          <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900">Outros serviços visíveis</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {serviceCards.slice(3).map((service) => (
                <div key={service.key} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">{service.title}</p>
                    <p className="mt-1 text-sm text-slate-600">{service.isReady ? "Pronto para uso" : "Disponível para consulta"}</p>
                  </div>
                  <Button variant="ghost" onClick={() => service.href && setLocation(service.href)} disabled={!service.href}>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </SalviaLayout>
  );
}
